import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { LayoutDashboard } from 'lucide-react'
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute'
import { ModuleGate } from '@/features/auth/components/ModuleGate'
import { OperatorRoute } from '@/features/auth/components/OperatorRoute'
import { useAuthStore } from '@/lib/auth.store'
import { AppLayout } from '@/components/layout/AppLayout'

// Auth pages
import LoginPage from '@/features/auth/pages/LoginPage'
import ForgotPasswordPage from '@/features/auth/pages/ForgotPasswordPage'
import ResetPasswordPage from '@/features/auth/pages/ResetPasswordPage'

// Users & Profile
import UsersPage from '@/features/users/pages/UsersPage'
import ProfilePage from '@/features/users/pages/ProfilePage'
// AP-18 Nachtrag 1: eigenes Paket — die Markdown-Bibliothek lädt nur, wer die Hilfe öffnet.
const HilfePage = lazy(() => import('@/features/help/pages/HilfePage'))
import EmployeeAppPage from '@/features/users/pages/EmployeeAppPage'
import AcceptInvitePage from '@/features/users/pages/AcceptInvitePage'
import ZweiFaktorEinrichtenPage from '@/features/auth/pages/ZweiFaktorEinrichtenPage'
import AcceptAccessPage from '@/features/users/pages/AcceptAccessPage'

// Tenant settings
import TenantSettingsPage from '@/features/tenants/pages/TenantSettingsPage'
import MailDispatchLogPage from '@/features/tenants/pages/MailDispatchLogPage'

// Clients
import ClientsPage from '@/features/clients/pages/ClientsPage'
import ClientDetailPage from '@/features/clients/pages/ClientDetailPage'

// Artikelverwaltung (AP-28)
import ArticlesPage from '@/features/articles/pages/ArticlesPage'
import ArticleDetailPage from '@/features/articles/pages/ArticleDetailPage'
import ArticleNewPage from '@/features/articles/pages/ArticleNewPage'
import ArticleListsPage from '@/features/articles/pages/ArticleListsPage'
import ArticleImportPage from '@/features/articles/pages/ArticleImportPage'
import ArticleSettingsPage from '@/features/articles/pages/ArticleSettingsPage'
import ArticleDashboardPage from '@/features/articles/pages/ArticleDashboardPage'

// Belegwesen (AP-32)
import DocumentListPage from '@/features/correspondence/pages/DocumentListPage'
import DocumentDashboardPage from '@/features/correspondence/pages/DocumentDashboardPage'
import DocumentPage from '@/features/correspondence/pages/DocumentPage'
import DocumentSettingsPage from '@/features/correspondence/pages/DocumentSettingsPage'
import DocumentTextsPage from '@/features/correspondence/pages/DocumentTextsPage'
import DocumentLayoutPage from '@/features/correspondence/pages/DocumentLayoutPage'
import DunningPage from '@/features/correspondence/pages/DunningPage'

// Payroll (AP-21-G: Geruest · AP-22-B: Stammdaten · AP-22-D: Erfassung)
import PayrollPage from '@/features/payroll/pages/PayrollPage'
import WorkSchedulesPage from '@/features/payroll/pages/WorkSchedulesPage'
import WorkScheduleDetailPage from '@/features/payroll/pages/WorkScheduleDetailPage'
import HolidaysPage from '@/features/payroll/pages/HolidaysPage'
import PayrollSettingsPage from '@/features/payroll/pages/PayrollSettingsPage'
import PayrollEntryPage from '@/features/payroll/pages/PayrollEntryPage'
import PayrollExportPage from '@/features/payroll/pages/PayrollExportPage'
import PayrollAbsenceTypesPage from '@/features/payroll/pages/PayrollAbsenceTypesPage'
import PayrollAccessPage from '@/features/payroll/pages/PayrollAccessPage'
import PayrollEmployeesPage from '@/features/payroll/pages/PayrollEmployeesPage'
import PayrollMonthsPage from '@/features/payroll/pages/PayrollMonthsPage'
import PayrollBmdPage from '@/features/payroll/pages/PayrollBmdPage'
import PayrollCompensationPage from '@/features/payroll/pages/PayrollCompensationPage'

// Time Tracking
import ClockPage from '@/features/time-tracking/pages/ClockPage'
import TimeDashboardPage from '@/features/time-tracking/pages/TimeDashboardPage'
import SummaryPage from '@/features/time-tracking/pages/SummaryPage'
import DailyWorkTimesPage from '@/features/time-tracking/pages/DailyWorkTimesPage'
import CurrentActivityPage from '@/features/time-tracking/pages/CurrentActivityPage'
import TimeConfigPage from '@/features/time-tracking/pages/TimeConfigPage'
import MonthlyReportConfigPage from '@/features/time-tracking/pages/MonthlyReportConfigPage'
import MonthlyReportOverviewPage from '@/features/time-tracking/pages/MonthlyReportOverviewPage'
import MonthlyReportPage from '@/features/time-tracking/pages/MonthlyReportPage'
import DataPathPage from '@/features/time-tracking/pages/DataPathPage'
import SelectionListsPage from '@/features/time-tracking/pages/SelectionListsPage'
import BetriebsstaettenPage from '@/features/time-tracking/pages/BetriebsstaettenPage'
import GruppenPage from '@/features/time-tracking/pages/GruppenPage'
import FieldOptionsPage from '@/features/time-tracking/pages/FieldOptionsPage'
import SelectionDataPage from '@/features/time-tracking/pages/SelectionDataPage'
import ProjectDetailPage from '@/features/projects/pages/ProjectDetailPage'
import ProjectSettingsPage from '@/features/projects/pages/ProjectSettingsPage'
import CalculationSettingsPage from '@/features/calculations/pages/CalculationSettingsPage'
import CalculationsPage from '@/features/calculations/pages/CalculationsPage'
import CalculationDetailPage from '@/features/calculations/pages/CalculationDetailPage'
import MaterialRequestsPage from '@/features/calculations/pages/MaterialRequestsPage'
// AP-18 7.5: Was die Kalkulation unter eine Antwort des Assistenten zeichnet, meldet sie beim Start an —
// hier und nicht in ihrer Seite, damit es auch unter dem Menüpunkt „Assistent" gilt, ohne dass je eine
// Kalkulation geöffnet war.
import '@/features/calculations/assistent-beilagen'
import { AssistentFrage } from '@/components/assistent/AssistentFrage'
import InboxPage from '@/features/inbox/pages/InboxPage'
import IncomingInvoicesPage from '@/features/incoming-invoices/pages/IncomingInvoicesPage'
import IncomingInvoiceDetailPage from '@/features/incoming-invoices/pages/IncomingInvoiceDetailPage'
import IncomingSettingsPage from '@/features/incoming-invoices/pages/IncomingSettingsPage'
import IncomingPaymentRunsPage from '@/features/incoming-invoices/pages/IncomingPaymentRunsPage'
import IncomingHandoverPage from '@/features/incoming-invoices/pages/IncomingHandoverPage'
import IncomingOverheadPage from '@/features/incoming-invoices/pages/IncomingOverheadPage'
import IncomingDashboardPage from '@/features/incoming-invoices/pages/IncomingDashboardPage'
import SiteDocsDashboardPage from '@/features/site-docs/pages/SiteDocsDashboardPage'
import SiteDocsListPage from '@/features/site-docs/pages/SiteDocsListPage'
import MontageMaengelPage from '@/features/site-docs/pages/MontageMaengelPage'
import BesprechungOffenePunktePage from '@/features/site-docs/pages/BesprechungOffenePunktePage'
import BestaetigenPage from '@/features/site-docs/pages/BestaetigenPage'
import PruefenPage from '@/features/site-docs/pages/PruefenPage'
import GegenzeichnenPage from '@/features/site-docs/pages/GegenzeichnenPage'
import BautagebuchPage from '@/features/site-docs/pages/BautagebuchPage'
import BehinderungenPage from '@/features/site-docs/pages/BehinderungenPage'
import SiteDocDetailPage from '@/features/site-docs/pages/SiteDocDetailPage'
import SiteDocsSettingsPage from '@/features/site-docs/pages/SiteDocsSettingsPage'
import InboxSettingsPage from '@/features/inbox/pages/InboxSettingsPage'
import CalculationDashboardPage from '@/features/calculations/pages/CalculationDashboardPage'
import ProjectDashboardPage from '@/features/projects/pages/ProjectDashboardPage'
import ProjectTasksPage from '@/features/projects/pages/ProjectTasksPage'
import MaterialCertificatesPage from '@/features/projects/pages/MaterialCertificatesPage'
import PurchasingPage from '@/features/projects/pages/PurchasingPage'
import ProjectBillingPage from '@/features/projects/pages/ProjectBillingPage'
import WorkCockpitPage from '@/features/projects/pages/WorkCockpitPage'
import UserSettingsPage from '@/features/time-tracking/pages/UserSettingsPage'
import { QrTagsPage } from '@/features/time-tracking/pages/QrTagsPage'
import BrandingPage from '@/features/branding/pages/BrandingPage'

// Fehlzeiten (AP-19)
import AbsencesPage from '@/features/absence/pages/AbsencesPage'
import AbsenceTypesPage from '@/features/absence/pages/AbsenceTypesPage'

// Integration (AP-09)
import ApiKeysPage from '@/features/integration/pages/ApiKeysPage'
import SyncLogPage from '@/features/integration/pages/SyncLogPage'

// Settings — Modules & AI (AP-11-C)
import ModulesPage from '@/features/settings/pages/ModulesPage'
import ModuleSeatsPage from '@/features/settings/pages/ModuleSeatsPage'
// AP-30-E: Rollen und Rechte des Mandanten
import RolesPage from '@/features/settings/pages/RolesPage'
import AiSettingsPage from '@/features/ai/pages/AiSettingsPage'
import AssistantPage from '@/features/ai/pages/AssistantPage'

// Projects (AP-09-B)
import ProjectsPage from '@/features/projects/pages/ProjectsPage'

// Time Master Data / Ref-Layer (AP-09-B)
import TimeProjectRefsPage from '@/features/time-master-data/pages/TimeProjectRefsPage'
import TimeProjectRefFormPage from '@/features/time-master-data/pages/TimeProjectRefFormPage'
import TimeProjectPositionRefsPage from '@/features/time-master-data/pages/TimeProjectPositionRefsPage'
import TimeProjectPositionRefFormPage from '@/features/time-master-data/pages/TimeProjectPositionRefFormPage'
import TimeProjectStepRefsPage from '@/features/time-master-data/pages/TimeProjectStepRefsPage'
import TimeProjectStepRefFormPage from '@/features/time-master-data/pages/TimeProjectStepRefFormPage'
import TimeActivityRefsPage from '@/features/time-master-data/pages/TimeActivityRefsPage'
import TimeActivityRefFormPage from '@/features/time-master-data/pages/TimeActivityRefFormPage'
import TimeUserRefsPage from '@/features/time-master-data/pages/TimeUserRefsPage'
import TimeUserRefFormPage from '@/features/time-master-data/pages/TimeUserRefFormPage'
import TimeRefSchemasPage from '@/features/time-master-data/pages/TimeRefSchemasPage'
import ScanRulesPage from '@/features/time-scan/pages/ScanRulesPage'

// Betreiber-Konsole (AP-29-E)
import OperatorTenantsPage from '@/features/operator/pages/OperatorTenantsPage'
import OperatorTenantModulesPage from '@/features/operator/pages/OperatorTenantModulesPage'
import OperatorPackagesPage from '@/features/operator/pages/OperatorPackagesPage'
import OperatorPricesPage from '@/features/operator/pages/OperatorPricesPage'
import OperatorZweiFaktorPage from '@/features/operator/pages/OperatorZweiFaktorPage'
import OperatorKiVorgabenPage from '@/features/operator/pages/OperatorKiVorgabenPage'
import OperatorVertraegePage from '@/features/operator/pages/OperatorVertraegePage'
import VertraegePage from '@/features/contracts/pages/VertraegePage'
import StoragePage from '@/features/storage/pages/StoragePage'
import OperatorLoeschprotokollPage from '@/features/operator/pages/OperatorLoeschprotokollPage'

function Dashboard() {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  return (
    <div className="p-6">
      <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
        <LayoutDashboard className="h-6 w-6" aria-hidden /> {t('dashboard.title')}
      </h1>
      <p className="mt-2 text-muted-foreground">
        {t('dashboard.welcome')}{user?.profile?.firstName ? `, ${user.profile.firstName}` : ''}!
      </p>
      {/* AP-18 5.3: Eingabefeld des Assistenten mit Beispielfragen quer über die Bereiche. */}
      <AssistentFrage className="mt-6 max-w-3xl" />
    </div>
  )
}


export default function App() {
  const { loadUser } = useAuthStore()

  useEffect(() => {
    loadUser()
  }, [loadUser])

  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        {/* AP-38-E: nach der Frist verlangt das Erneuern die Einrichtung — öffentlich, mit Ticket. */}
        <Route path="/zwei-faktor-einrichten" element={<ZweiFaktorEinrichtenPage />} />
        {/* AP-46 22./23.: Bestätigung per Link und Prüfseite — der Token ist der einzige Zugang. */}
        <Route path="/bestaetigen/:token" element={<BestaetigenPage />} />
        <Route path="/pruefen/:token" element={<PruefenPage />} />
        {/* AP-48 RN-6: Sammel-Gegenzeichnung per Link — ebenso nur über das Token. */}
        <Route path="/gegenzeichnen/:token" element={<GegenzeichnenPage />} />

        {/* Protected routes inside AppLayout */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            {/*
              AP-29-D: Ein Wächter für alle Pfad-Äste. Die Zuordnung Pfad → Modul
              steht in ModuleGate und kommt aus der Registry; eine neue
              Unterseite unter einem bestehenden Bereich ist damit automatisch
              mitgeschützt. Deshalb trägt hier keine einzelne Route mehr eine
              Modul-Eigenschaft.
            */}
            <Route element={<ModuleGate />}>
            {/* All authenticated users */}
            <Route path="/" element={<Dashboard />} />
            <Route path="/profile" element={<ProfilePage />} />
            {/* AP-18 Nachtrag 1: Hilfe für alle — was jemand sieht, filtert das Backend je Thema. */}
            {['/hilfe', '/hilfe/:bereich', '/hilfe/:bereich/:thema'].map((pfad) => (
              <Route key={pfad} path={pfad} element={<Suspense fallback={null}><HilfePage /></Suspense>} />
            ))}
            {/* AP-38-B: Zugang zu einer weiteren Firma — hinter der Anmeldung, der Token kommt über state.from zurück */}
            <Route path="/accept-access" element={<AcceptAccessPage />} />
            {/* AP-31: Die Seite hängt am Leserecht, nicht nur am Modul-Gate. Ein
                Mandant, der seinem Aushilfspersonal die Partnerliste wegnimmt,
                soll das können — `crm.client.read` steht in jeder Vorlage, die
                Sperre ist also eine Entscheidung, kein Entzug. */}
            <Route element={<ProtectedRoute requiredPermission="crm.client.read" />}>
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/clients/:id" element={<ClientDetailPage />} />
            </Route>
            {/* AP-28: Das Modul-Gate hängt am Ast `/articles` (Registry `areas`),
                das Recht an der Route — dasselbe wie am Endpunkt. */}
            <Route element={<ProtectedRoute requiredPermission="articles.article.read" />}>
              <Route path="/articles" element={<ArticlesPage />} />
              {/* AP-40: Dashboard der Artikel — Startseite des Menüpunkts. */}
              <Route path="/articles/dashboard" element={<ArticleDashboardPage />} />
              <Route path="/articles/stammlisten" element={<ArticleListsPage />} />
              <Route path="/articles/:id" element={<ArticleDetailPage />} />
            </Route>
            {/* Anlegen ist ein leeres Blatt statt eines Dialogs (13.9.2026) — und braucht das Pflegerecht. */}
            <Route element={<ProtectedRoute requiredPermission="articles.article.manage" />}>
              <Route path="/articles/neu" element={<ArticleNewPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="articles.article.import" />}>
              <Route path="/articles/import" element={<ArticleImportPage />} />
            </Route>
            {/* AP-28 Nachtrag N-17: Nummernaufbau und Bildgröße — eigenes Recht, beim Administrator. */}
            <Route element={<ProtectedRoute requiredPermission="articles.settings.manage" />}>
              <Route path="/articles/einstellungen" element={<ArticleSettingsPage />} />
            </Route>
            {/* AP-32: Das Modul-Gate hängt am Ast `/correspondence`, die Tür am Recht
                `correspondence.document.read`; welche Arten jemand sieht, entscheidet
                der Dienst je Beleg. */}
            <Route element={<ProtectedRoute requiredPermission="correspondence.document.read" />}>
              <Route path="/correspondence" element={<DocumentListPage />} />
              {/* AP-40: Dashboard des Schriftverkehrs — Startseite des Menüpunkts. */}
              <Route path="/correspondence/dashboard" element={<DocumentDashboardPage />} />
              <Route path="/correspondence/:id" element={<DocumentPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="correspondence.settings.manage" />}>
              <Route path="/correspondence/settings" element={<DocumentSettingsPage />} />
              <Route path="/correspondence/settings/texts" element={<DocumentTextsPage />} />
              <Route path="/correspondence/settings/layout" element={<DocumentLayoutPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="correspondence.dunning.create" />}>
              <Route path="/correspondence/dunning" element={<DunningPage />} />
            </Route>
            {/* AP-35: Die Liste und das Detail hängen am Leserecht, die
                Einstellungen an ihrem eigenen (Detailblatt 1 6., Blatt 13 4.1). */}
            {/* AP-35 Phase D: Modul-Dashboard und die Nav-Seite Aufgaben.
                „Meine Aufgaben" steht jedem Benutzer mit dem Baustein offen —
                auch ohne Leserecht am Projekt (Blatt 13 4.3). */}
            <Route path="/project-management" element={<ProjectDashboardPage />} />
            <Route path="/project-tasks" element={<ProjectTasksPage />} />
            <Route path="/work-cockpit" element={<WorkCockpitPage />} />
            <Route path="/material-certificates" element={<MaterialCertificatesPage />} />
            <Route path="/purchasing" element={<PurchasingPage />} />
            <Route path="/project-billing" element={<ProjectBillingPage />} />
            {/* AP-35 Nachtrag 5: „Projekte" öffnet das Detailblatt im leeren Zustand, die Liste hat einen eigenen Menüpunkt. */}
            <Route path="/projects" element={<ProjectDetailPage />} />
            <Route path="/projects/list" element={<ProjectsPage />} />
            <Route element={<ProtectedRoute requiredPermission="projects.settings.manage" />}>
              <Route path="/projects/settings" element={<ProjectSettingsPage />} />
            </Route>
            <Route path="/projects/:id" element={<ProjectDetailPage />} />
            {/* AP-37: Kalkulation — der Ast `/calculations` hängt am ModuleGate der Gruppe. */}
            <Route element={<ProtectedRoute requiredPermission="calculation.settings.manage" />}>
              <Route path="/calculations/settings" element={<CalculationSettingsPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="calculation.calculation.read" />}>
              <Route path="/calculations" element={<CalculationsPage />} />
              <Route path="/calculations/templates" element={<CalculationsPage vorlagen />} />
              {/* Detailblatt O: vor `/calculations/:id`, sonst läse die Detailseite „material-requests" als Kennung. */}
              <Route path="/calculations/material-requests" element={<MaterialRequestsPage />} />
              {/* AP-40: das Dashboard der Kalkulation — ebenfalls vor `/calculations/:id`. */}
              <Route path="/calculations/dashboard" element={<CalculationDashboardPage />} />
              <Route path="/calculations/:id" element={<CalculationDetailPage />} />
            </Route>
            {/* AP-39: Posteingang — der Ast `/inbox` hängt am ModuleGate der Gruppe INBOX. */}
            <Route element={<ProtectedRoute requiredPermission="inbox.item.read" />}>
              <Route path="/inbox" element={<InboxPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="inbox.settings.manage" />}>
              <Route path="/inbox/settings" element={<InboxSettingsPage />} />
            </Route>
            {/* AP-34: Eingangsrechnungen — der Ast `/incoming-invoices` hängt am ModuleGate der Gruppe INCOMING_INVOICES. */}
            <Route element={<ProtectedRoute requiredPermission="incoming_invoices.invoice.read" />}>
              <Route path="/incoming-invoices/dashboard" element={<IncomingDashboardPage />} />
              <Route path="/incoming-invoices" element={<IncomingInvoicesPage />} />
              {/* vor `/incoming-invoices/:id`, sonst läse die Erfassung „inbox" als Kennung */}
              <Route path="/incoming-invoices/inbox" element={<IncomingInvoicesPage stapel />} />
              <Route path="/incoming-invoices/approvals" element={<IncomingInvoicesPage freigaben />} />
              <Route path="/incoming-invoices/open-items" element={<IncomingInvoicesPage offen />} />
              <Route element={<ProtectedRoute requiredPermission="incoming_invoices.invoice.read.all" />}>
                <Route path="/incoming-invoices/overhead" element={<IncomingOverheadPage />} />
              </Route>
              <Route element={<ProtectedRoute requiredPermission="incoming_invoices.export.create" />}>
                <Route path="/incoming-invoices/handover" element={<IncomingHandoverPage />} />
              </Route>
              <Route element={<ProtectedRoute requiredPermission="incoming_invoices.payment.manage" />}>
                <Route path="/incoming-invoices/payment-runs" element={<IncomingPaymentRunsPage />} />
              </Route>
              <Route element={<ProtectedRoute requiredPermission="incoming_invoices.settings.manage" />}>
                <Route path="/incoming-invoices/settings" element={<IncomingSettingsPage />} />
              </Route>
              <Route path="/incoming-invoices/:id" element={<IncomingInvoiceDetailPage />} />
            </Route>
            <Route path="/time-tracking" element={<ClockPage />} />
            {/* AP-40: Dashboard der Zeiterfassung; `/time-tracking` bleibt „Buchen“ (MD-5). */}
            <Route path="/time-tracking/dashboard" element={<TimeDashboardPage />} />

            {/* Lohnverrechnung. Das Modul-Gate haengt seit AP-29-D am Pfad
                (/payroll → PAYROLL) und zeigt die Seite „kein Zugang" statt
                wortlos aufs Dashboard umzuleiten. AP-22-B: Stammdaten — jede
                Seite verlangt ihr eigenes Lese-Recht (AP-30). */}
            {/* AP-40 7.6: Die Startseite ist das Dashboard des Bereichs; `/payroll` bleibt
                für Lesezeichen und leitet dorthin. */}
            <Route element={<ProtectedRoute requiredPermission="payroll.status.read" />}>
              <Route path="/payroll/dashboard" element={<PayrollPage />} />
              <Route path="/payroll" element={<Navigate to="/payroll/dashboard" replace />} />
            </Route>
            {/* AP-22 Teil 2, J: Stand je Mitarbeiter und Monat. */}
            <Route element={<ProtectedRoute requiredPermission="payroll.month.read" />}>
              <Route path="/payroll/months" element={<PayrollMonthsPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="payroll.bmd.export" />}>
              <Route path="/payroll/bmd" element={<PayrollBmdPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="payroll.collective_agreement.read" />}>
              <Route path="/payroll/compensation" element={<PayrollCompensationPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="payroll.work_schedule.read" />}>
              <Route path="/payroll/work-schedules" element={<WorkSchedulesPage />} />
              <Route path="/payroll/work-schedules/:id" element={<WorkScheduleDetailPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="payroll.holiday.read" />}>
              <Route path="/payroll/holidays" element={<HolidaysPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="payroll.setting.read" />}>
              <Route path="/payroll/settings" element={<PayrollSettingsPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="payroll.day_entry.read" />}>
              <Route path="/payroll/entry" element={<PayrollEntryPage />} />
              <Route path="/payroll/export" element={<PayrollExportPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="payroll.absence_type.read" />}>
              <Route path="/payroll/absence-types" element={<PayrollAbsenceTypesPage />} />
            </Route>
            {/* AP-38-F: Mitarbeiter, auch ohne Zugang — für den Betrieb „nur Lohnverrechnung". */}
            <Route element={<ProtectedRoute requiredPermission="payroll.employee.read" />}>
              <Route path="/payroll/employees" element={<PayrollEmployeesPage />} />
            </Route>
            {/* AP-38-D: wer die Lohnverrechnung macht — intern und extern. */}
            <Route element={<ProtectedRoute requiredPermission="payroll.access.manage" />}>
              <Route path="/payroll/access" element={<PayrollAccessPage />} />
            </Route>
            {/* Fehlzeiten – alle Buchungsrollen erfassen eigene, READONLY liest (Schreib-Buttons ausgeblendet) */}
            <Route path="/time-tracking/absences" element={<AbsencesPage />} />
            {/* Eigener Monatsbericht (Mitarbeiter-Abgabe, wie in der PWA) – alle Buchungsrollen */}
            <Route path="/time-tracking/monthly-report" element={<MonthlyReportPage />} />

            {/* AP-29-E: Betreiber-Konsole — kein Modul, keine Rolle, sondern die
                Zugehörigkeit zum Betreiber-Mandanten. Die Sperre selbst sitzt im
                PlatformAdminGuard des Backends. */}
            <Route element={<OperatorRoute />}>
              <Route path="/admin/mandanten" element={<OperatorTenantsPage />} />
              <Route path="/admin/mandanten/:tenantId" element={<OperatorTenantModulesPage />} />
              <Route path="/admin/pakete" element={<OperatorPackagesPage />} />
              <Route path="/admin/preise" element={<OperatorPricesPage />} />
              <Route path="/admin/zwei-faktor" element={<OperatorZweiFaktorPage />} />
              {/* AP-43: KI-Vorgaben — Modell je Aufgabe für alle Mandanten ohne eigene Wahl */}
              <Route path="/admin/ki-vorgaben" element={<OperatorKiVorgabenPage />} />
              {/* AP-41: Verträge — Dokumente, Fassungen, Stand je Mandant */}
              <Route path="/admin/vertraege" element={<OperatorVertraegePage />} />
              <Route path="/admin/loeschprotokoll" element={<OperatorLoeschprotokollPage />} />
            </Route>

            {/* AP-30-D: Eine Route verlangt das Recht, das ihr Endpunkt verlangt —
                nicht mehr eine Rollen-Untergrenze. Aus der einen Gruppe „MANAGER
                aufwärts" werden dadurch vier Zeilen, und das ist der Gewinn: Wer
                Benutzer verwaltet, muss keine Tagesarbeitszeiten sehen. */}
            <Route element={<ProtectedRoute requiredPermission="core.user.read" />}>
              <Route path="/users" element={<UsersPage />} />
              <Route path="/mitarbeiter-app" element={<EmployeeAppPage />} />
            </Route>
            {/* AP-46: Baustellendokumente — die Äste `/site-docs` und `/settings/site-docs` hängen am ModuleGate (SITE_DOCS.CORE). */}
            <Route element={<ProtectedRoute requiredPermission="sitedocs.document.read" />}>
              <Route path="/site-docs/dashboard" element={<SiteDocsDashboardPage />} />
              <Route path="/site-docs" element={<SiteDocsListPage />} />
              {/* AP-47 6.2: Mängelliste — hängt über `areas` am Baustein `SITE_DOCS.ASSEMBLY`. */}
              <Route path="/site-docs/defects" element={<MontageMaengelPage />} />
              {/* AP-48 7.: Bautagebuch (Kalender) und Behinderungsliste — die Seiten prüfen den Baustein SITE_DOCS.DIARY selbst. */}
              <Route path="/site-docs/diary" element={<BautagebuchPage />} />
              <Route path="/site-docs/diary/hindrances" element={<BehinderungenPage />} />
              <Route path="/site-docs/meeting/open-items" element={<BesprechungOffenePunktePage />} />
              <Route path="/site-docs/:id" element={<SiteDocDetailPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="sitedocs.settings.manage" />}>
              <Route path="/settings/site-docs" element={<SiteDocsSettingsPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="core.role.manage" />}>
              {/* AP-30-E: Rollen und Rechte des Mandanten */}
              <Route path="/settings/roles" element={<RolesPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="time.entry.read_running" />}>
              <Route path="/time-tracking/current" element={<CurrentActivityPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="time.daily_work_time.read" />}>
              <Route path="/time-tracking/daily-work-times" element={<DailyWorkTimesPage />} />
              {/* Alte Route bis 6.9.2026: „Lohnverrechnung". Umleitung, damit
                  bestehende Lesezeichen nicht ins Leere laufen. */}
              <Route path="/time-tracking/payroll" element={<Navigate to="/time-tracking/daily-work-times" replace />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="time.report.overview" />}>
              <Route path="/time-tracking/monthly-report-overview" element={<MonthlyReportOverviewPage />} />
            </Route>

            {/* Einträge-Liste: der **volle Geltungsbereich**, nicht die Grundform.
                Vorher stand hier eine Rollen-Allowlist mit MANAGER und READONLY —
                die eine Stelle, an der die Leiter sichtbar versagte, weil READONLY
                unterhalb von EMPLOYEE liegt und trotzdem mehr sehen sollte. Mit
                `time.entry.read.all` ist derselbe Kreis eine Angabe: Manager und
                „Nur Lesen" haben ihn, ein Mitarbeiter hat `.own` und bleibt
                draußen — genau wie bisher. */}
            <Route element={<ProtectedRoute requiredPermission="time.entry.read.all" />}>
              <Route path="/time-tracking/summary" element={<SummaryPage />} />
            </Route>

            {/* Mandanten-Einstellungen — je Seite das Recht ihres Endpunkts */}
            <Route element={<ProtectedRoute requiredPermission="core.tenant.update" />}>
              <Route path="/settings" element={<TenantSettingsPage />} />
            </Route>
            {/* AP-10 Nachtrag 5: Versandprotokoll der Exporte */}
            <Route element={<ProtectedRoute requiredPermission="core.mail.manage" />}>
              <Route path="/settings/mail-log" element={<MailDispatchLogPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="core.branding.manage" />}>
              <Route path="/settings/branding" element={<BrandingPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="core.module.read" />}>
              <Route path="/settings/modules" element={<ModulesPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="core.seat.manage" />}>
              {/* AP-29-H: Plaetze eines gebuchten Moduls verteilen */}
              <Route path="/settings/modules/:moduleKey/plaetze" element={<ModuleSeatsPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="ai.config.read" />}>
              <Route path="/settings/ai" element={<AiSettingsPage />} />
            </Route>
            {/* AP-18: Assistent — der Ast `/assistant` hängt am ModuleGate des Bausteins AI_ASSISTANT. */}
            <Route element={<ProtectedRoute requiredPermission="ai.assistant.use" />}>
              <Route path="/assistant" element={<AssistantPage />} />
              <Route path="/assistant/:id" element={<AssistantPage />} />
            </Route>
            {/* AP-11-E4: Speicher des Betriebs — belegt, Kontingent, Aufteilung */}
            <Route element={<ProtectedRoute requiredPermission="core.storage.read" />}>
              <Route path="/settings/storage" element={<StoragePage />} />
            </Route>
            {/* AP-41: Verträge des Betriebs annehmen */}
            <Route element={<ProtectedRoute requiredPermission="core.contract.accept" />}>
              <Route path="/settings/vertraege" element={<VertraegePage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="integration.api_key.read" />}>
              <Route path="/integration/api-keys" element={<ApiKeysPage />} />
              <Route path="/integration/sync-log" element={<SyncLogPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="time.qr_tag.read" />}>
              <Route path="/time-tracking/qr-tags" element={<QrTagsPage />} />
            </Route>

            {/* Der Ast „Einrichtung der Zeiterfassung".
                Maske und Endpunkt trugen hier verschiedene Rechte: Die
                Ref-Schicht (Projekte, Positionen, Arbeitsschritte, Tätigkeiten)
                hing an Endpunkten ganz ohne Rollenprüfung, während die Seiten
                dazu immer ADMIN-only waren. Der eigene Zug ist am 12.9.2026
                gefahren — `time.project_ref.manage` und `time.user_ref.manage`
                stehen nur noch in der Administrator-Vorlage, und die Masken
                dazu tragen **dasselbe** Recht wie ihre Endpunkte (siehe unten).
                Eine Maske, die ein anderes Recht verlangt als der Endpunkt
                dahinter, ist entweder eine Tür ohne Schloss oder ein Schloss
                ohne Tür. */}
            {/* KA-46: Seiten, deren Endpunkte ein anderes Recht als time.config.manage tragen, stehen an diesem. */}
            <Route element={<ProtectedRoute requiredPermission="time.report.configure" />}>
              <Route element={<ProtectedRoute requiredPermission="time.config.manage" />}>
                {/* Der Katalog braucht report.configure, das Speichern config.manage — beides. */}
                <Route path="/time-tracking/monthly-report-config" element={<MonthlyReportConfigPage />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute requiredPermission="time.absence_type.manage" />}>
              <Route path="/time-tracking/absence-types" element={<AbsenceTypesPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="time.user_settings.manage" />}>
              <Route path="/time-tracking/user-settings" element={<UserSettingsPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="time.selection.manage" />}>
              <Route path="/time-tracking/lists" element={<SelectionListsPage />} />
              <Route path="/time-tracking/lists/betriebsstaette" element={<BetriebsstaettenPage />} />
              <Route path="/time-tracking/lists/groups" element={<GruppenPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="time.config.manage" />}>
              <Route path="/time-tracking/config" element={<TimeConfigPage />} />
              {/* Menü-Neuordnung 2026-06: geführter Datenweg-Assistent */}
              <Route path="/time-tracking/data-path" element={<DataPathPage />} />
              {/* „Eigene Felder & Auswahllisten" — neuer Menüname; alte URL bleibt als Alias erhalten */}
              <Route path="/time-tracking/fields" element={<SelectionDataPage />} />
              <Route path="/time-tracking/selection-data" element={<SelectionDataPage />} />
              {/* KA-46: „Konfigurationen" gibt es nicht mehr — Stundensätze, QR-Codes und Scan-Regeln stehen im Menü. */}
              <Route path="/time-tracking/configs" element={<Navigate to="/time-tracking/user-settings" replace />} />
              <Route path="/time-tracking/lists/field-options" element={<FieldOptionsPage />} />
              <Route path="/integration/data-sources" element={<Navigate to="/time-tracking/data-path" replace />} />

              <Route path="/time-tracking/master-data" element={<Navigate to="/time-tracking/lists" replace />} />
              {/* Gelernte Auftrags-Scans sind entfallen (21.9.2026) — alte Lesezeichen landen bei den Regeln */}
              <Route path="/time-tracking/scan-mappings" element={<Navigate to="/time-tracking/scan-rules" replace />} />
              {/* AP-14-B: Scan-Regeln (Struktur-Definition) */}
              <Route path="/time-tracking/scan-rules" element={<ScanRulesPage />} />
            </Route>

            {/* Time Master Data / Ref-Layer (AP-09-B / AP-13) — jede Maske am
                Recht ihres eigenen Endpunkts. */}
            <Route element={<ProtectedRoute requiredPermission="time.project_ref.manage" />}>
              <Route path="/time-tracking/projects" element={<TimeProjectRefsPage />} />
              <Route path="/time-tracking/projects/new" element={<TimeProjectRefFormPage />} />
              <Route path="/time-tracking/projects/:id/edit" element={<TimeProjectRefFormPage />} />
              <Route path="/time-tracking/positions" element={<TimeProjectPositionRefsPage />} />
              <Route path="/time-tracking/positions/new" element={<TimeProjectPositionRefFormPage />} />
              <Route path="/time-tracking/positions/:id/edit" element={<TimeProjectPositionRefFormPage />} />
              <Route path="/time-tracking/steps" element={<TimeProjectStepRefsPage />} />
              <Route path="/time-tracking/steps/new" element={<TimeProjectStepRefFormPage />} />
              <Route path="/time-tracking/steps/:id/edit" element={<TimeProjectStepRefFormPage />} />
              <Route path="/time-tracking/activity-types" element={<TimeActivityRefsPage />} />
              <Route path="/time-tracking/activity-types/new" element={<TimeActivityRefFormPage />} />
              <Route path="/time-tracking/activity-types/:id/edit" element={<TimeActivityRefFormPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="time.user_ref.manage" />}>
              <Route path="/time-tracking/users" element={<TimeUserRefsPage />} />
              <Route path="/time-tracking/users/new" element={<TimeUserRefFormPage />} />
              <Route path="/time-tracking/users/:id/edit" element={<TimeUserRefFormPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredPermission="time.ref_schema.manage" />}>
              <Route path="/time-tracking/ref-schemas" element={<TimeRefSchemasPage />} />
            </Route>
            </Route>
          </Route>
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  )
}
